/**
 * How a policy opens: on its own, or beside the list it came from.
 *
 * Two ways of working, and which one is right depends on the job rather than
 * on taste. Reading one policy somebody sent you wants the whole width.
 * Working through sixty overdue ones wants the list to stay put.
 *
 * NOT "SIDE BY SIDE". The split shows one policy beside the list, which is
 * a kept place, not several documents open at once. It was labelled "Side by
 * side" for a while and the owner rightly objected: to him that phrase means
 * a tabbed, multi-document arrangement, which is a separate build for later.
 * The label says what the thing does.
 *
 * DEFAULT IS ONE AT A TIME. It is the simpler thing, it is what the
 * application did before, and it is the one that cannot surprise somebody who
 * has not asked for anything.
 */

import { usePersistentChoice } from '../../shared/usePersistentChoice';
import { SegmentedChoice } from '../../shared/SegmentedChoice';

export const POLICY_LAYOUTS = ['single', 'split'] as const;
export type PolicyLayout = (typeof POLICY_LAYOUTS)[number];

export const DEFAULT_POLICY_LAYOUT: PolicyLayout = 'single';

const LABELS: Record<PolicyLayout, string> = {
  single: 'One at a time',
  split: 'Keep the list',
};

const DESCRIPTIONS: Record<PolicyLayout, string> = {
  single: 'Each policy opens on its own, using the full width.',
  split: 'Policies open beside the list you came from, so you keep your place.',
};

export function usePolicyLayout() {
  return usePersistentChoice<PolicyLayout>(
    'policyLayout',
    POLICY_LAYOUTS,
    DEFAULT_POLICY_LAYOUT,
  );
}

/**
 * The control. See shared/SegmentedChoice for why it is a radio group and not
 * a pair of buttons.
 */
export function PolicyLayoutChoice({
  value,
  onChange,
  /** Distinguishes the two instances on a page from each other. */
  idPrefix = 'policy-layout',
}: {
  value: PolicyLayout;
  onChange: (next: PolicyLayout) => void;
  idPrefix?: string;
}) {
  return (
    <SegmentedChoice
      legend="Open policies"
      name={idPrefix}
      value={value}
      onChange={onChange}
      options={POLICY_LAYOUTS.map((layout) => ({
        value: layout,
        label: LABELS[layout],
        description: DESCRIPTIONS[layout],
      }))}
    />
  );
}
